import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BookOpen,
  Search,
  Clock,
  Star,
  Download,
  Filter,
  Loader2,
} from "lucide-react";
import { marked } from "marked";
import {
  LegalResearchRequest,
  DraftRequest,
  DraftResponse,
  DownloadDraftRequest,
  LegalResearchResponse,
} from "@/apiClient/data-contracts";
import { toast } from "@/components/ui/use-toast";
import { API_BASE_URL } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { supabase } from "@/lib/supabase";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";

interface SearchFilters {
  jurisdiction: string[];
  documentType: string[];
  legalTopic: string[];
}

interface SearchResult {
  id: string;
  title: string;
  type: string;
  date: string;
  jurisdiction: string;
  content: string;
  tags: string[];
  relevance: number;
  url?: string;
  court?: string;
  author?: string;
  source?: string;
}

interface ResearchStatistics {
  total_cases_reviewed?: number;
  total_legislations_reviewed?: number;
  total_articles_reviewed?: number;
  main_sources_consulted?: string[];
  areas_of_law_addressed?: string[];
}

interface ApiError {
  status?: number;
  message?: string;
}

interface RecentSearch {
  id: string;
  query: string;
  date: string;
  keywords?: string;
  jurisdiction?: string;
  document_types?: string[];
  legal_topics?: string[];
}

// Enhanced case interface for the new response structure
interface EnhancedCase {
  title: string;
  court: string;
  date: string;
  jurisdiction: string;
  summary: string;
  tags: string[];
  relevance: number;
  url: string;
  key_holdings?: string;
  impact?: string;
  supporting_jurisprudence?: string[];
  legal_thesis?: string;
  extract?: string;
  dissenting_vote?: {
    magistrate: string;
    vote_count: string;
    thesis: string;
    extract: string;
  };
  clarification_vote?: {
    magistrate: string;
    vote_count: string;
    thesis: string;
    extract: string;
  };
  content?: string;
}

// Enhanced jurisprudence interface
interface EnhancedJurisprudence {
  case_number: string;
  topic: string;
  summary: string;
  decision_date: string;
  url: string;
  [key: string]: string;
}

// Enhanced statistics interface
interface EnhancedStatistics {
  sources_found: number;
  search_time: string;
  relevance_distribution: Record<string, unknown>;
  methodology_used: string;
}

// Extend the imported interface to include the actual API response properties
interface ExtendedLegalResearchResponse extends LegalResearchResponse {
  cases?: EnhancedCase[];
  legislation?: Record<string, unknown>[];
  articles?: Record<string, unknown>[];
  summary?: string;
  statistics?: EnhancedStatistics;
  jurisprudence?: EnhancedJurisprudence[];
  colombian_compliance: {
    framework_version: string;
    constitutional_principles: string[];
    research_date: string;
  };
  legal_framework: Record<string, string[]>;
  specific_areas?: string[];
  data_processing?: Record<string, string> | null;
  legal_terms?: Record<string, string> | null;
  pdf_summary_url?: string | null;
  timestamp?: string;
}

export function LegalResearch() {
  const { openDocument } = useDocumentViewer();
  // Search form state
  const [query, setQuery] = useState("");
  const [keywords, setKeywords] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<SearchFilters>({
    jurisdiction: [],
    documentType: [],
    legalTopic: [],
  });

  // Check if required tables exist
  const checkAndCreateTables = async () => {
    try {
      // Check if legal_research_summaries table exists by trying to select from it
      const { error: checkError } = await supabase
        .from("legal_research_summaries")
        .select("id")
        .limit(1);

      if (checkError) {
        console.warn(
          "legal_research_summaries table may not exist:",
          checkError
        );
        setError(
          "Algunas tablas de investigación legal no están disponibles. Contacte al administrador."
        );
        toast({
          title: "Configuración de base de datos",
          description:
            "Algunas tablas de investigación legal no están disponibles. Contacte al administrador.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error checking database tables:", error);
      setError("Error al verificar la configuración de la base de datos.");
    }
  };

  // Results state
  const { startResearchRun, getRun } = useAgentExecution();
  const researchRunId = "legal-research-run";
  const currentRun = getRun(researchRunId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSearching = (currentRun?.status === "loading") || isLoading;
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [activeTab, setActiveTab] = useState("cases");
  const [sortBy, setSortBy] = useState<"recent" | "relevant">("relevant");

  const [localResearchResponse, setLocalResearchResponse] = useState<ExtendedLegalResearchResponse | null>(null);
  const researchResponse = (currentRun?.metadata as ExtendedLegalResearchResponse) || localResearchResponse;

  const { user } = useAuth();
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const apiClient = useApiClient();

  // Case expansion state
  const [expandedCases, setExpandedCases] = useState<Set<number>>(new Set());


  // Function to save search results to database
  const saveSearchResultsToDatabase = async (
    searchId: string,
    response: ExtendedLegalResearchResponse
  ) => {
    try {
      // Starting to save search results to database

      // Save cases
      if (response.cases && response.cases.length > 0) {
        try {
          const casesData = response.cases.map(
            (case_: EnhancedCase) => ({
              search_id: searchId,
              result_type: "case",
              title: case_.title,
              summary: case_.summary,
              content: case_.content || case_.summary || "",
              url: case_.url,
              date: case_.date,
              jurisdiction: case_.jurisdiction,
              court: case_.court,
              tags: case_.tags,
              relevance: case_.relevance,
              metadata: case_ as unknown as Record<string, unknown>,
            })
          );

          const { data: casesResult, error: casesError } = await supabase
            .from("legal_research_results")
            .insert(casesData)
            .select();

          if (casesError) {
            console.error("Error inserting cases:", casesError);
            throw casesError;
          }

          // Cases inserted successfully
        } catch (error) {
          console.error("Error saving cases:", error);
          throw error;
        }
      } else {
        // No cases data in response to save
      }

      // Save legislation
      if (response.legislation && response.legislation.length > 0) {
        try {
          const legislationData = response.legislation.map(
            (leg: Record<string, unknown>) => ({
              search_id: searchId,
              result_type: "legislation",
              title: leg.title as string,
              summary: leg.summary as string,
              content: leg.content as string,
              url: leg.url as string,
              date: leg.date as string,
              jurisdiction: leg.jurisdiction as string,
              tags: leg.tags as string[],
              relevance: 0.98,
              metadata: leg,
            })
          );

          const { data: legislationResult, error: legislationError } =
            await supabase
              .from("legal_research_results")
              .insert(legislationData)
              .select();

          if (legislationError) {
            console.error("Error inserting legislation:", legislationError);
            throw legislationError;
          }

          // Legislation inserted successfully
        } catch (error) {
          console.error("Error saving legislation:", error);
          throw error;
        }
      }

      // Save articles
      if (response.articles && response.articles.length > 0) {
        try {
          const articlesData = response.articles.map(
            (article: Record<string, unknown>) => ({
              search_id: searchId,
              result_type: "article",
              title: article.title as string,
              summary: article.summary as string,
              content: article.content as string,
              url: article.url as string,
              date: article.date as string,
              jurisdiction: response.jurisdiction as string,
              author: article.author as string,
              source: article.source as string,
              tags: article.tags as string[],
              relevance: 0.95,
              metadata: article,
            })
          );

          const { data: articlesResult, error: articlesError } = await supabase
            .from("legal_research_results")
            .insert(articlesData)
            .select();

          if (articlesError) {
            console.error("Error inserting articles:", articlesError);
            throw articlesError;
          }

          // Articles inserted successfully
        } catch (error) {
          console.error("Error saving articles:", error);
          throw error;
        }
      } else {
        // No articles data in response to save
      }

      // Save summary
      if (response.summary || response.statistics) {
        try {
          const summaryData = {
            search_id: searchId,
            summary: response.summary || null,
            statistics: response.statistics || null,
            legal_framework: response.legal_framework || null,
            jurisprudence: response.jurisprudence || null,
            constitutional_principles:
              response.colombian_compliance?.constitutional_principles || [],
            specific_areas: response.specific_areas || [],
          };

          // Inserting summary data

          const { data: summaryResult, error: summaryError } = await supabase
            .from("legal_research_summaries")
            .insert(summaryData)
            .select();

          if (summaryError) {
            console.error("Error inserting summary:", summaryError);
            throw summaryError;
          }

          // Summary inserted successfully
        } catch (error) {
          console.error("Error saving summary to database:", error);
          // Don't throw here, continue with the process
        }
      } else {
      }

      // Search results saved to database successfully
    } catch (error) {
      console.error("Error saving search results to database:", error);
      setError(
        "Error al guardar los resultados en la base de datos. Los resultados se mostrarán pero no se guardarán."
      );
      toast({
        title: "Error al guardar",
        description:
          "Error al guardar los resultados en la base de datos. Los resultados se mostrarán pero no se guardarán.",
        variant: "destructive",
      });
    }
  };

  // Function to load search results from database
  const loadSearchResultsFromDatabase = async (searchId: string) => {
    try {
      setIsLoading(true);

      // Load search details
      const { data: searchData, error: searchError } = await supabase
        .from("legal_research_searches")
        .select("*")
        .eq("id", searchId)
        .single();

      if (searchError) {
        console.error("Error loading search:", searchError);
        return;
      }

      // Load search results
      const { data: resultsData, error: resultsError } = await supabase
        .from("legal_research_results")
        .select("*")
        .eq("search_id", searchId)
        .order("created_at", { ascending: true });

      if (resultsError) {
        console.error("Error loading results:", resultsError);
        return;
      }

      // Load summary
      let summaryData = null;
      try {
        const { data: summaryResult, error: summaryError } = await supabase
          .from("legal_research_summaries")
          .select("*")
          .eq("search_id", searchId)
          .single();

        if (summaryError) {
          if (summaryError.code === "PGRST116") {
            // No rows returned - this is normal for new searches
            // No summary found for search
          } else {
            console.error("Error loading summary:", summaryError);
            console.error("Summary error details:", {
              code: summaryError.code,
              message: summaryError.message,
              details: summaryError.details,
              hint: summaryError.hint,
            });
          }
        } else {
          summaryData = summaryResult;
          // Summary loaded successfully
        }
      } catch (error) {
        console.error("Unexpected error loading summary:", error);
      }

      // Transform results back to the expected format
      const transformedResults: SearchResult[] = resultsData.map((result) => ({
        id: result.id,
        title: result.title,
        type: result.result_type as "case" | "legislation" | "article",
        date: result.date || "",
        jurisdiction: result.jurisdiction || "",
        content: result.summary || "",
        tags: result.tags || [],
        relevance: (result.relevance || 0) * 100,
        url: result.url,
        court: result.court,
        author: result.author,
        source: result.source,
      }));

      // Reconstruct the research response
      const reconstructedResponse: ExtendedLegalResearchResponse = {
        research_topic: searchData.query,
        jurisdiction: searchData.jurisdiction,
        specific_areas: searchData.legal_topics || [],
        legal_terms: searchData.keywords
          ? searchData.keywords.split(",").map((term) => term.trim())
          : [],
        research_results: {},
        cases: resultsData
          .filter((r) => r.result_type === "case")
          .map((r) => r.metadata),
        legislation: resultsData
          .filter((r) => r.result_type === "legislation")
          .map((r) => r.metadata),
        articles: resultsData
          .filter((r) => r.result_type === "article")
          .map((r) => r.metadata),
        summary: summaryData?.summary || "Resumen no disponible",
        statistics: summaryData?.statistics || {},
        legal_framework: summaryData?.legal_framework || {},
        jurisprudence: summaryData?.jurisprudence || [],
        colombian_compliance: {
          framework_version: summaryData?.framework_version || "1.0",
          research_date: summaryData?.research_date || new Date().toISOString(),
          constitutional_principles:
            summaryData?.constitutional_principles || [],
        },
      };

      setSearchResults(transformedResults);
      setLocalResearchResponse(reconstructedResponse);
      setQuery(searchData.query);
      setKeywords(searchData.keywords || "");
      setSelectedFilters({
        jurisdiction: searchData.jurisdiction ? [searchData.jurisdiction] : [],
        documentType: searchData.document_types || [],
        legalTopic: searchData.legal_topics || [],
      });

      toast({
        title: "Búsqueda cargada",
        description:
          "Los resultados de la búsqueda anterior han sido cargados.",
      });
    } catch (error) {
      console.error("Error loading search results:", error);
      setError(
        "No se pudieron cargar los resultados de la búsqueda anterior. Intente nuevamente."
      );
      toast({
        title: "Error al cargar búsqueda",
        description:
          "No se pudieron cargar los resultados de la búsqueda anterior.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check tables and fetch recent searches from Supabase on mount or user change
  useEffect(() => {
    const initializeComponent = async () => {
      if (!user) return;

      // Check if required tables exist
      await checkAndCreateTables();

      // Fetch recent searches
      const { data, error } = await supabase
        .from("legal_research_searches")
        .select(
          "id, query, keywords, jurisdiction, document_types, legal_topics, created_at"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!error && data) {
        setRecentSearches(
          data.map((row) => ({
            id: row.id,
            query: row.query,
            date: new Date(row.created_at).toLocaleDateString("es-ES"),
            keywords: row.keywords,
            jurisdiction: row.jurisdiction,
            document_types: row.document_types,
            legal_topics: row.legal_topics,
          }))
        );
      }
    };
    initializeComponent();
  }, [user]);

  const handleFilterSelect = (category: keyof SearchFilters, value: string) => {
    setSelectedFilters((prev) => {
      const filters = { ...prev };
      if (category === "jurisdiction" || category === "legalTopic") {
        // Single select: select or deselect
        filters[category] = filters[category][0] === value ? [] : [value];
      } else {
        // Multi-select for documentType
        if (filters[category].includes(value)) {
          filters[category] = filters[category].filter(
            (item) => item !== value
          );
        } else {
          filters[category] = [...filters[category], value];
        }
      }
      return filters;
    });
  };

  const handleSearch = async () => {
    if (!query.trim() || !user) return;

    setError(null);
    setLocalResearchResponse(null);
    setSearchResults([]);

    const request: LegalResearchRequest = {
      research_topic: query,
      jurisdiction: selectedFilters.jurisdiction[0] || "Colombia",
      specific_areas: selectedFilters.legalTopic,
      legal_terms: keywords
        .split(",")
        .map((term) => term.trim())
        .filter(Boolean),
    };

    await startResearchRun(researchRunId, request, apiClient, user.id);
  };

  // Transform results when researchResponse changes (from context or local)
  useEffect(() => {
    if (!researchResponse) return;

    const transformedResults: SearchResult[] = [];

    // Transform cases
    const cases = researchResponse.cases || [];
    cases.forEach((case_: EnhancedCase, index: number) => {
      transformedResults.push({
        id: `case-${index}`,
        title: case_.title,
        type: "case",
        date: case_.date,
        jurisdiction: case_.jurisdiction,
        content: case_.summary,
        tags: case_.tags,
        relevance: case_.relevance * 100,
        url: case_.url,
        court: case_.court,
      });
    });

    // Transform legislation
    const legislation = researchResponse.legislation || [];
    legislation.forEach((leg: Record<string, unknown>, index: number) => {
      transformedResults.push({
        id: `legislation-${index}`,
        title: leg.title as string,
        type: "legislation",
        date: leg.date as string,
        jurisdiction: leg.jurisdiction as string,
        content: leg.summary as string,
        tags: leg.tags as string[],
        relevance: 98,
        url: leg.url as string,
      });
    });

    // Transform articles
    const articles = researchResponse.articles || [];
    articles.forEach((article: Record<string, unknown>, index: number) => {
      transformedResults.push({
        id: `article-${index}`,
        title: article.title as string,
        type: "article",
        date: article.date as string,
        jurisdiction: researchResponse.jurisdiction as string,
        content: article.summary as string,
        tags: article.tags as string[],
        relevance: 95,
        url: article.url as string,
        author: article.author as string,
        source: article.source as string,
      });
    });

    setSearchResults(transformedResults);
  }, [researchResponse]);

  const handleGenerateDraft = async () => {
    if (!researchResponse) return;

    try {
      setError(null); // Clear any previous errors
      const draftRequest: DraftRequest = {
        research_topic: researchResponse.research_topic,
        document_type: selectedFilters.documentType[0] || "informe",
        research_results: researchResponse.research_results,
        legal_framework: researchResponse.legal_framework,
        jurisprudence: researchResponse.jurisprudence || [],
      };

      const response =
        await apiClient.dashboardClient.generateDraftDashboardLegalResearchGenerateDraftPost(
          draftRequest
        );

      if (response.data) {
        // Handle the draft response
        toast({
          title: "Borrador generado",
          description: "El borrador ha sido generado exitosamente.",
        });
      }
    } catch (error: unknown) {
      console.error("Error generating draft:", error);
      const apiError = error as ApiError;
      if (apiError?.status === 429 || apiError?.status === 500) {
        setError(
          "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve."
        );
        toast({
          title: "Error al generar borrador",
          description:
            "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve.",
          variant: "destructive",
        });
      } else {
        setError(
          "Hubo un error al generar el borrador. Por favor, intente nuevamente."
        );
        toast({
          title: "Error al generar borrador",
          description:
            "Hubo un error al generar el borrador. Por favor, intente nuevamente.",
          variant: "destructive",
        });
      }
    }
  };

  const handleDownloadDraft = async (format: "docx" | "pdf" | "txt") => {
    if (!researchResponse) return;

    try {
      setError(null); // Clear any previous errors
      const downloadRequest: DownloadDraftRequest = {
        content: JSON.stringify(researchResponse),
        format,
        document_type: "investigacion_legal",
        filename: `investigacion_legal_${new Date().toISOString().split("T")[0]
          }`,
      };

      await apiClient.dashboardClient.downloadDraftDashboardLegalResearchDownloadDraftPost(
        downloadRequest
      );

      toast({
        title: "Descarga iniciada",
        description: "El documento se está descargando.",
      });
    } catch (error: unknown) {
      console.error("Error downloading draft:", error);
      const apiError = error as ApiError;
      if (apiError?.status === 429 || apiError?.status === 500) {
        setError(
          "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve."
        );
        toast({
          title: "Error al descargar",
          description:
            "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve.",
          variant: "destructive",
        });
      } else {
        setError(
          "Hubo un error al descargar el documento. Por favor, intente nuevamente."
        );
        toast({
          title: "Error al descargar",
          description:
            "Hubo un error al descargar el documento. Por favor, intente nuevamente.",
          variant: "destructive",
        });
      }
    }
  };

  // Function to remove a recent search
  const handleRemoveRecentSearch = async (
    searchId: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent triggering the load function

    try {
      // Delete the search and all related data
      const { error: searchError } = await supabase
        .from("legal_research_searches")
        .delete()
        .eq("id", searchId);

      if (searchError) {
        console.error("Error removing search:", searchError);
        toast({
          title: "Error al eliminar búsqueda",
          description: "No se pudo eliminar la búsqueda. Intente nuevamente.",
          variant: "destructive",
        });
        return;
      }

      // Remove from local state
      setRecentSearches((prev) =>
        prev.filter((search) => search.id !== searchId)
      );

      toast({
        title: "Búsqueda eliminada",
        description: "La búsqueda ha sido eliminada de su historial.",
      });
    } catch (error) {
      console.error("Error removing search:", error);
      toast({
        title: "Error al eliminar búsqueda",
        description: "Ocurrió un error al eliminar la búsqueda.",
        variant: "destructive",
      });
    }
  };

  // Function to clear all recent searches
  const handleClearAllRecentSearches = async () => {
    try {
      // Delete all searches for the current user
      const { error } = await supabase
        .from("legal_research_searches")
        .delete()
        .eq("user_id", user?.id);

      if (error) {
        console.error("Error clearing searches:", error);
        toast({
          title: "Error al limpiar historial",
          description: "No se pudo limpiar el historial. Intente nuevamente.",
          variant: "destructive",
        });
        return;
      }

      // Clear local state
      setRecentSearches([]);

      toast({
        title: "Historial limpiado",
        description: "Todo el historial de búsquedas ha sido eliminado.",
      });
    } catch (error) {
      console.error("Error clearing searches:", error);
      toast({
        title: "Error al limpiar historial",
        description: "Ocurrió un error al limpiar el historial.",
        variant: "destructive",
      });
    }
  };

  const handleSortChange = (type: "recent" | "relevant") => {
    setSortBy(type);
    const sorted = [...searchResults].sort((a, b) => {
      if (type === "recent") {
        // Parse dates robustly
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      }
      // Ensure relevance is a number
      const relA =
        typeof a.relevance === "string"
          ? parseFloat(a.relevance)
          : a.relevance || 0;
      const relB =
        typeof b.relevance === "string"
          ? parseFloat(b.relevance)
          : b.relevance || 0;
      return relB - relA;
    });
    setSearchResults(sorted);
  };

  const FilterButton = ({
    category,
    value,
    isSelected,
  }: {
    category: keyof SearchFilters;
    value: string;
    isSelected: boolean;
  }) => (
    <Button
      variant={isSelected ? "default" : "outline"}
      size="sm"
      className="rounded-full"
      onClick={() => handleFilterSelect(category, value)}>
      {value}
    </Button>
  );

  // Toggle case expansion
  const toggleCaseExpansion = (index: number) => {
    setExpandedCases((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Check if we should show compact view (more than 3 cases)
  const shouldShowCompactView = (researchResponse?.cases?.length || 0) > 3;

  // Expand all cases
  const expandAllCases = () => {
    if (researchResponse?.cases) {
      const allIndices = researchResponse.cases.map((_, index) => index);
      setExpandedCases(new Set(allIndices));
    }
  };

  // Collapse all cases
  const collapseAllCases = () => {
    setExpandedCases(new Set());
  };

  return (
    <div>
      {/* Page Title and Subtitle */}
      <div className="ml-8">
        <h1 className="text-3xl font-bold tracking-tight">
          Asistente de Investigación Legal
        </h1>
        <p className="text-muted-foreground mt-1 mb-6">
          Búsqueda potenciada por NLP en jurisprudencia, leyes y regulaciones
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="ml-8 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error en la investigación legal
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="bg-red-50 text-red-700 hover:bg-red-100 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Flex row: Form Card and Sidebar */}
      <div className="flex flex-row gap-8">
        {/* Left: Search Form Card */}
        <div className="flex-1 min-w-0 ml-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Nueva consulta legal</CardTitle>
              <CardDescription>
                Describa su consulta con el mayor detalle posible para obtener
                resultados más precisos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium">Jurisdicción</p>
                <div className="flex flex-wrap gap-2">
                  {["Estatal", "Local", "Internacional"].map((item) => (
                    <FilterButton
                      key={item}
                      category="jurisdiction"
                      value={item}
                      isSelected={selectedFilters.jurisdiction.includes(item)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Tipo de documento</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Jurisprudencia",
                    "Legislación",
                    "Doctrina",
                    "Precedentes",
                  ].map((item) => (
                    <FilterButton
                      key={item}
                      category="documentType"
                      value={item}
                      isSelected={selectedFilters.documentType.includes(item)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Tema legal</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Civil",
                    "Penal",
                    "Administrativo",
                    "Laboral",
                    "Mercantil",
                    "Constitucional",
                    "Familia",
                    "Tributario",
                    "Propiedad Intelectual",
                    "Ambiental",
                    "Salud",
                    "Seguridad Social",
                    "Migratorio",
                    "Comercial",
                    "Penal Económico",
                    "TIC",
                    "Energía",
                    "Transporte",
                    "Minero",
                    "Agrario",
                  ].map((item) => (
                    <FilterButton
                      key={item}
                      category="legalTopic"
                      value={item}
                      isSelected={selectedFilters.legalTopic.includes(item)}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="query" className="text-sm font-medium">
                    Su consulta detallada
                  </label>
                  <span className="text-xs text-gray-500">
                    {query.length}/3000 caracteres
                  </span>
                </div>
                <Textarea
                  id="query"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Describa su consulta legal aquí. Por ejemplo: Jurisprudencia reciente sobre casos de indemnización por despido injustificado en sector tecnológico..."
                  className="min-h-[120px]"
                  maxLength={3000}
                />
              </div>

              <div>
                <div className="flex justify-between items-center">
                  <label htmlFor="keywords" className="text-sm font-medium">
                    Palabras clave (opcional)
                  </label>
                  <span className="text-xs text-gray-500">
                    {keywords.length}/500 caracteres
                  </span>
                </div>
                <Input
                  id="keywords"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="despido, indemnización, sector tecnológico..."
                  className="mt-1"
                  maxLength={500}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleGenerateDraft}
                  disabled={!researchResponse}>
                  Guardar borrador
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuery("");
                    setKeywords("");
                    setSelectedFilters({
                      jurisdiction: [],
                      documentType: [],
                      legalTopic: [],
                    });
                    setSearchResults([]);
                    setLocalResearchResponse(null);
                    setActiveTab("cases");
                  }}
                  className="gap-2">
                  <Search size={16} />
                  Nueva búsqueda
                </Button>
              </div>
              <Button
                onClick={handleSearch}
                disabled={isSearching || !query.trim()}
                className="gap-2">
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search size={18} />
                )}
                {isSearching ? "Buscando..." : "Buscar"}
              </Button>
            </CardFooter>
          </Card>
          {searchResults.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold mb-4">
                Resultados de investigación
              </h2>

              <div className="flex justify-between mb-4">
                <div className="flex gap-2 items-center">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Filter size={16} /> Filtrar
                  </Button>
                  <Button
                    variant={sortBy === "recent" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleSortChange("recent")}>
                    Más reciente
                  </Button>
                  <Button
                    variant={sortBy === "relevant" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleSortChange("relevant")}>
                    Más relevante
                  </Button>
                  {shouldShowCompactView && (
                    <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full border border-blue-200">
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Vista compacta activada
                    </div>
                  )}
                  {shouldShowCompactView && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={expandAllCases}
                        className="gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        Expandir todo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={collapseAllCases}
                        className="gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                        Contraer todo
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => handleDownloadDraft("docx")}>
                    <Download size={16} /> DOCX
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => handleDownloadDraft("pdf")}>
                    <Download size={16} /> PDF
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-5 mb-4">
                  <TabsTrigger value="cases">
                    Casos ({researchResponse?.cases?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="legislation">
                    Legislación ({researchResponse?.legislation?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="articles">
                    Artículos ({researchResponse?.articles?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="jurisprudence">
                    Jurisprudencia (
                    {researchResponse?.jurisprudence?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="summarize">Resumen</TabsTrigger>
                </TabsList>

                <TabsContent value="cases" className="space-y-4">
                  {researchResponse?.cases?.map(
                    (case_: EnhancedCase, index: number) => {
                      const isExpanded = expandedCases.has(index);
                      const showCompact = shouldShowCompactView && !isExpanded;

                      return (
                        <Card key={`case-${index}`} className="overflow-hidden">
                          <CardContent className="p-6">
                            {/* Header Section */}
                            <div className="flex justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="font-bold text-lg text-gray-900 mb-1">
                                  {case_.title}
                                </h3>
                                <div className="flex gap-4 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock size={14} /> {case_.date}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <BookOpen size={14} /> {case_.court}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                    {case_.jurisdiction}
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0">
                                  <Star size={16} />
                                </Button>
                                {case_.url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() =>
                                      openDocument(case_.url, case_.title)
                                    }>
                                    <Download size={16} />
                                  </Button>
                                )}
                                {shouldShowCompactView && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => toggleCaseExpansion(index)}
                                    title={
                                      isExpanded
                                        ? "Contraer detalles"
                                        : "Expandir detalles"
                                    }>
                                    {isExpanded ? (
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M9 5l7 7-7 7"
                                        />
                                      </svg>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Summary Section - Always visible */}
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                Resumen
                              </h4>
                              <p
                                className={`text-gray-600 leading-relaxed ${showCompact ? "line-clamp-3" : ""
                                  }`}>
                                {case_.summary}
                              </p>
                            </div>

                            {/* Detailed sections - Only show when expanded or when there are 3 or fewer cases */}
                            {(!shouldShowCompactView || isExpanded) && (
                              <>
                                {/* Key Holdings Section */}
                                {case_.key_holdings && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                      Tesis Principales
                                    </h4>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                      <p className="text-blue-800 text-sm leading-relaxed">
                                        {case_.key_holdings}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Legal Thesis Section */}
                                {case_.legal_thesis && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                      Tesis Legal
                                    </h4>
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                      <p className="text-green-800 text-sm leading-relaxed">
                                        {case_.legal_thesis}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Impact Section */}
                                {case_.impact && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                      Impacto
                                    </h4>
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                      <p className="text-orange-800 text-sm leading-relaxed">
                                        {case_.impact}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Extract Section */}
                                {case_.extract && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                      Extracto Relevante
                                    </h4>
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                      <p className="text-gray-700 text-sm leading-relaxed italic">
                                        "{case_.extract}"
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Supporting Jurisprudence */}
                                {case_.supporting_jurisprudence &&
                                  case_.supporting_jurisprudence.length > 0 && (
                                    <div className="mb-4">
                                      <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                        Jurisprudencia de Apoyo
                                      </h4>
                                      <div className="flex flex-wrap gap-2">
                                        {case_.supporting_jurisprudence.map(
                                          (ref, idx) => (
                                            <span
                                              key={idx}
                                              className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full border border-purple-200">
                                              {ref}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {/* Dissenting Vote */}
                                {case_.dissenting_vote &&
                                  case_.dissenting_vote.magistrate && (
                                    <div className="mb-4">
                                      <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                        Voto Salvado
                                      </h4>
                                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-red-800 text-sm">
                                          <strong>Magistrado:</strong>{" "}
                                          {case_.dissenting_vote.magistrate}
                                        </p>
                                        {case_.dissenting_vote.thesis && (
                                          <p className="text-red-700 text-sm mt-2">
                                            {case_.dissenting_vote.thesis}
                                          </p>
                                        )}
                                        {case_.dissenting_vote.extract && (
                                          <p className="text-red-600 text-xs mt-2 italic">
                                            "{case_.dissenting_vote.extract}"
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {/* Clarification Vote */}
                                {case_.clarification_vote &&
                                  case_.clarification_vote.magistrate && (
                                    <div className="mb-4">
                                      <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                        Voto Aclaratorio
                                      </h4>
                                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                        <p className="text-yellow-800 text-sm">
                                          <strong>Magistrado:</strong>{" "}
                                          {case_.clarification_vote.magistrate}
                                        </p>
                                        {case_.clarification_vote.thesis && (
                                          <p className="text-yellow-700 text-sm mt-2">
                                            {case_.clarification_vote.thesis}
                                          </p>
                                        )}
                                        {case_.clarification_vote.extract && (
                                          <p className="text-yellow-600 text-xs mt-2 italic">
                                            "{case_.clarification_vote.extract}"
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </>
                            )}

                            {/* Tags Section - Always visible but compact when collapsed */}
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                Etiquetas
                              </h4>
                              <div
                                className={`flex flex-wrap gap-1 ${showCompact ? "max-h-8 overflow-hidden" : ""
                                  }`}>
                                {case_.tags.map((tag: string) => (
                                  <span
                                    key={tag}
                                    className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                    {tag}
                                  </span>
                                ))}
                                {showCompact && case_.tags.length > 3 && (
                                  <span className="text-xs text-gray-500 italic">
                                    +{case_.tags.length - 3} más...
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Footer Section */}
                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                              <span className="text-sm font-medium">
                                Relevancia:{" "}
                                <span
                                  className={`${case_.relevance >= 0.9
                                    ? "text-green-600"
                                    : case_.relevance >= 0.7
                                      ? "text-amber-600"
                                      : "text-red-600"
                                    }`}>
                                  {Math.round(case_.relevance * 100)}%
                                </span>
                              </span>
                              {case_.url && (
                                <Button
                                  onClick={() =>
                                    openDocument(case_.url, case_.title)
                                  }
                                  className="gap-2">
                                  <Download size={16} />
                                  Ver completo
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }
                  )}
                </TabsContent>

                <TabsContent value="legislation" className="space-y-4">
                  {researchResponse?.legislation?.map(
                    (leg: Record<string, unknown>, index: number) => (
                      <Card key={`legislation-${index}`}>
                        <CardContent className="p-6">
                          <div className="flex justify-between mb-2">
                            <h3 className="font-bold text-lg">
                              {leg.title as string}
                            </h3>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0">
                                <Star size={16} />
                              </Button>
                              {leg.url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() =>
                                    openDocument(leg.url as string, leg.title as string)
                                  }>
                                  <Download size={16} />
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> Última reforma:{" "}
                              {leg.date as string}
                            </span>
                          </div>

                          <p className="mb-3">{leg.summary as string}</p>

                          <div className="flex flex-wrap gap-1 mb-3">
                            {(leg.tags as string[]).map((tag: string) => (
                              <span
                                key={tag}
                                className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>

                          <div className="flex justify-between items-center">
                            {leg.url && (
                              <Button
                                onClick={() =>
                                  openDocument(leg.url as string, leg.title as string)
                                }>
                                Ver completo
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  )}
                </TabsContent>

                <TabsContent value="articles" className="space-y-4">
                  {researchResponse?.articles?.map(
                    (article: Record<string, unknown>, index: number) => (
                      <Card key={`article-${index}`}>
                        <CardContent className="p-6">
                          <div className="flex justify-between mb-2">
                            <h3 className="font-bold text-lg">
                              {article.title as string}
                            </h3>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0">
                                <Star size={16} />
                              </Button>
                              {article.url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() =>
                                    openDocument(article.url as string, article.title as string)
                                  }>
                                  <Download size={16} />
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Clock size={14} /> Publicado:{" "}
                              {article.date as string}
                            </span>
                            <span className="flex items-center gap-1">
                              <BookOpen size={14} /> {article.source as string}
                            </span>
                          </div>

                          <p className="mb-3">{article.summary as string}</p>

                          <div className="flex flex-wrap gap-1 mb-3">
                            {(article.tags as string[]).map((tag: string) => (
                              <span
                                key={tag}
                                className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>

                          <div className="flex justify-between items-center">
                            {article.url && (
                              <Button
                                onClick={() =>
                                  openDocument(article.url as string, article.title as string)
                                }>
                                Ver completo
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  )}
                </TabsContent>

                <TabsContent value="jurisprudence" className="space-y-4">
                  {researchResponse?.jurisprudence?.map(
                    (jurisprudence: EnhancedJurisprudence, index: number) => (
                      <Card key={`jurisprudence-${index}`}>
                        <CardContent className="p-6">
                          <div className="flex justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900 mb-1">
                                {jurisprudence.case_number}
                              </h3>
                              <div className="flex gap-4 text-sm text-muted-foreground mb-3">
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />{" "}
                                  {jurisprudence.decision_date}
                                </span>
                                <span className="flex items-center gap-1">
                                  <BookOpen size={14} /> {jurisprudence.topic}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0">
                                <Star size={16} />
                              </Button>
                              {jurisprudence.url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() =>
                                    openDocument(jurisprudence.url, jurisprudence.case_number)
                                  }>
                                  <Download size={16} />
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="mb-4">
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">
                              Resumen
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                              {jurisprudence.summary}
                            </p>
                          </div>

                          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                            <span className="text-sm text-gray-500">
                              Jurisprudencia de referencia
                            </span>
                            {jurisprudence.url && (
                              <Button
                                onClick={() =>
                                  openDocument(jurisprudence.url, jurisprudence.case_number)
                                }
                                className="gap-2">
                                <Download size={16} />
                                Ver completo
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  )}
                  {(!researchResponse?.jurisprudence ||
                    researchResponse.jurisprudence.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No se encontró jurisprudencia relacionada</p>
                      </div>
                    )}
                </TabsContent>

                <TabsContent value="summarize">
                  {researchResponse && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Resumen de la investigación</CardTitle>
                        <CardDescription>
                          Análisis generado por IA sobre los resultados más
                          relevantes
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Main Summary Content */}
                        <div className="prose prose-sm max-w-none">
                          <style>{`
                            .markdown-content h1, .markdown-content h2, .markdown-content h3 {
                              color: #1f2937;
                              font-weight: 600;
                              margin-top: 1.5rem;
                              margin-bottom: 0.75rem;
                            }
                            .markdown-content h1 {
                              font-size: 1.5rem;
                              border-bottom: 2px solid #e5e7eb;
                              padding-bottom: 0.5rem;
                            }
                            .markdown-content h2 {
                              font-size: 1.25rem;
                              color: #374151;
                            }
                            .markdown-content h3 {
                              font-size: 1.1rem;
                              color: #4b5563;
                            }
                            .markdown-content p {
                              margin-bottom: 1rem;
                              line-height: 1.6;
                            }
                            .markdown-content ul {
                              margin-bottom: 1rem;
                              padding-left: 1.5rem;
                            }
                            .markdown-content li {
                              margin-bottom: 0.5rem;
                              line-height: 1.5;
                            }
                            .markdown-content strong {
                              color: #1f2937;
                              font-weight: 600;
                            }
                            .markdown-content a {
                              color: #2563eb;
                              text-decoration: underline;
                            }
                            .markdown-content a:hover {
                              color: #1d4ed8;
                            }
                             .markdown-content blockquote {
                               border-left: 4px solid #e5e7eb;
                               padding-left: 1rem;
                               margin: 1rem 0;
                               font-style: italic;
                               color: #6b7280;
                             }
                             
                             /* Line clamp utility for compact view */
                             .line-clamp-3 {
                               display: -webkit-box;
                               -webkit-line-clamp: 3;
                               -webkit-box-orient: vertical;
                               overflow: hidden;
                             }
                           `}</style>
                          <div
                            className="markdown-content"
                            dangerouslySetInnerHTML={{
                              __html: marked.parse(
                                researchResponse.summary ||
                                "Resumen no disponible"
                              ) as string,
                            }}
                          />
                        </div>

                        {/* Enhanced Statistics Section */}
                        <div className="mt-8">
                          <h4 className="font-semibold mb-4 text-lg">
                            Estadísticas de la investigación
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-600 font-medium">
                                Casos revisados
                              </p>
                              <p className="text-2xl font-bold text-blue-800">
                                {researchResponse.cases?.length || 0}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                              <p className="text-sm text-green-600 font-medium">
                                Legislación revisada
                              </p>
                              <p className="text-2xl font-bold text-green-800">
                                {researchResponse.legislation?.length || 0}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                              <p className="text-sm text-purple-600 font-medium">
                                Artículos revisados
                              </p>
                              <p className="text-2xl font-bold text-purple-800">
                                {researchResponse.articles?.length || 0}
                              </p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                              <p className="text-sm text-orange-600 font-medium">
                                Fuentes encontradas
                              </p>
                              <p className="text-2xl font-bold text-orange-800">
                                {researchResponse.statistics?.sources_found ||
                                  (researchResponse.cases?.length || 0) +
                                  (researchResponse.legislation?.length ||
                                    0) +
                                  (researchResponse.articles?.length || 0)}
                              </p>
                            </div>
                          </div>

                          {/* Additional Statistics from Backend */}
                          {researchResponse.statistics && (
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-3 rounded-lg border border-indigo-200">
                                <p className="text-sm text-indigo-600 font-medium">
                                  Tiempo de búsqueda
                                </p>
                                <p className="text-lg font-bold text-indigo-800">
                                  {researchResponse.statistics.search_time ||
                                    "N/A"}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-3 rounded-lg border border-emerald-200">
                                <p className="text-sm text-emerald-600 font-medium">
                                  Metodología
                                </p>
                                <p className="text-lg font-bold text-emerald-800">
                                  {researchResponse.statistics
                                    .methodology_used || "N/A"}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-3 rounded-lg border border-amber-200">
                                <p className="text-sm text-amber-600 font-medium">
                                  Jurisprudencia
                                </p>
                                <p className="text-lg font-bold text-amber-800">
                                  {researchResponse.jurisprudence?.length || 0}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Research Details */}
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Areas of Law */}
                          {researchResponse.specific_areas &&
                            researchResponse.specific_areas.length > 0 && (
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-semibold mb-3 text-gray-800">
                                  Áreas del derecho abordadas
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {researchResponse.specific_areas.map(
                                    (area) => (
                                      <span
                                        key={area}
                                        className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border border-gray-200 shadow-sm">
                                        {area}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Constitutional Principles */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold mb-3 text-gray-800">
                              Principios constitucionales aplicados
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {researchResponse.colombian_compliance?.constitutional_principles?.map(
                                (principle) => (
                                  <span
                                    key={principle}
                                    className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border border-gray-200 shadow-sm">
                                    {principle}
                                  </span>
                                )
                              ) || (
                                  <span className="text-gray-500 text-sm">
                                    No especificados
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>

                        {/* Research Metadata */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <h4 className="font-semibold mb-3 text-blue-800">
                            Información de la investigación
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-blue-600 font-medium">
                                Jurisdicción
                              </p>
                              <p className="text-blue-800">
                                {researchResponse.jurisdiction ||
                                  "No especificada"}
                              </p>
                            </div>
                            <div>
                              <p className="text-blue-600 font-medium">
                                Fecha de investigación
                              </p>
                              <p className="text-blue-800">
                                {researchResponse.colombian_compliance
                                  ?.research_date
                                  ? new Date(
                                    researchResponse.colombian_compliance.research_date
                                  ).toLocaleDateString("es-ES")
                                  : researchResponse.timestamp
                                    ? new Date(
                                      researchResponse.timestamp
                                    ).toLocaleDateString("es-ES")
                                    : "No especificada"}
                              </p>
                            </div>
                            <div>
                              <p className="text-blue-600 font-medium">
                                Versión del marco
                              </p>
                              <p className="text-blue-800">
                                {researchResponse.colombian_compliance
                                  ?.framework_version || "No especificada"}
                              </p>
                            </div>
                            <div>
                              <p className="text-blue-600 font-medium">
                                Metodología
                              </p>
                              <p className="text-blue-800">
                                {researchResponse.statistics
                                  ?.methodology_used || "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                      <CardFooter className="flex justify-end gap-2">
                        <Button
                          className="gap-2"
                          onClick={() => handleDownloadDraft("docx")}>
                          <Download size={18} /> Descargar informe DOCX
                        </Button>
                        <Button
                          className="gap-2"
                          onClick={() => handleDownloadDraft("pdf")}>
                          <Download size={18} /> Descargar informe PDF
                        </Button>
                      </CardFooter>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
        {/* Right: Recent Searches Sidebar */}
        <aside className="w-80 flex-shrink-0">
          <div className="bg-white rounded-2xl shadow p-4 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-700" /> Búsquedas recientes
              </h2>
              {recentSearches.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllRecentSearches}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs">
                  Limpiar todo
                </Button>
              )}
            </div>
            <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
              {recentSearches.length === 0 ? (
                <div className="text-gray-400 text-sm text-center py-8">
                  No hay búsquedas recientes.
                </div>
              ) : (
                recentSearches.map((s, idx) => (
                  <div
                    key={idx}
                    className="group relative flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <button
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                      onClick={() => loadSearchResultsFromDatabase(s.id)}>
                      <div className="flex-1 min-w-0">
                        <span className="truncate font-medium text-sm block">
                          {s.query}
                        </span>
                        <span className="text-xs text-gray-400">{s.date}</span>
                      </div>
                      <Search className="h-4 w-4 text-blue-400 ml-2 flex-shrink-0" />
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 ml-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleRemoveRecentSearch(s.id, e)}>
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </div>
                ))
              )}
            </div>
            {recentSearches.length > 0 && (
              <div className="text-xs text-gray-500 text-center border-t pt-2">
                Mostrando las últimas {recentSearches.length} búsquedas
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default LegalResearch;
